import { useState } from "react";
import { Link, useParams } from "wouter";
import { ArrowLeft, PenSquare } from "lucide-react";
import { useClub, useClubPosts, useJoinClub } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useToast } from "@/components/ux/Toast";
import { Loader } from "@/components/Loader";
import { ClubPostCard, GateNotice } from "./clubComponents";
import { useClubGate } from "./useClubGate";
import { ClubNewPostSheet } from "./ClubNewPostSheet";

export default function ClubForum() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useT();
  const toast = useToast();
  const { data: club, isLoading, error } = useClub(slug);
  const canRead = !!club?.access.can.readForum;
  const posts = useClubPosts(slug, canRead);
  const join = useJoinClub(slug);
  const gate = useClubGate();
  const [composerOpen, setComposerOpen] = useState(false);

  const back = (
    <Link href={`/clubs/${slug}`} className="btn-ghost inline-flex items-center gap-2">
      <ArrowLeft className="w-4 h-4" /> {club?.name ?? t("clubs.back")}
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className="pt-4 pb-24 md:pt-8 px-4 max-w-3xl mx-auto space-y-6">
        {back}
        <div className="card text-center py-12 text-dark-muted">{t("clubs.notFound")}</div>
      </div>
    );
  }

  const { access } = club;
  const joinClub = () =>
    join.mutate(true, {
      onSuccess: () => toast.success(t("clubs.joinedToast")),
      onError: (e) => toast.error(e.message),
    });
  const gateNotice = (
    <GateNotice
      message={access.isMember ? t("clubs.subscribeToPost") : t("clubs.joinToParticipate")}
      actionLabel={access.isMember ? t("clubs.subscribe") : t("clubs.join")}
      onAction={access.isMember ? gate.openSubscribe : joinClub}
    />
  );
  const forumPosts = posts.data?.pages.flatMap((p) => p.posts) ?? [];

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        {back}
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">{t("clubs.forum")}</h1>
          {access.can.writeForum && (
            <button onClick={() => setComposerOpen(true)} className="btn btn-primary btn-sm rounded-full gap-1.5">
              <PenSquare className="w-4 h-4" /> {t("clubs.newPost")}
            </button>
          )}
        </div>

        {!canRead ? (
          gateNotice
        ) : (
          <>
            {!access.can.writeForum && gateNotice}
            {posts.isLoading ? (
              <div className="flex justify-center py-6">
                <Loader />
              </div>
            ) : forumPosts.length === 0 ? (
              <p className="text-sm text-dark-muted text-center py-6">{t("clubs.noPosts")}</p>
            ) : (
              forumPosts.map((post) => <ClubPostCard key={post.id} post={post} slug={club.slug} />)
            )}
            {posts.hasNextPage && (
              <button onClick={() => posts.fetchNextPage()} disabled={posts.isFetchingNextPage} className="btn btn-ghost btn-sm w-full">
                {t("clubs.loadMore")}
              </button>
            )}
          </>
        )}
      </div>
      {access.can.writeForum && (
        <ClubNewPostSheet isOpen={composerOpen} onClose={() => setComposerOpen(false)} slug={club.slug} clubName={club.name} />
      )}
      {gate.interstitial}
    </div>
  );
}
